import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import NumberWithIndicator from './NumberWithIndicator';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getErrorMessageKey,
    getFormattedGaugeValue,
    getFormattedThresholdValue,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';
import {
    getIndicatorStatus,
    getIndicatorColorFromStatus,
} from './numberWithIndicatorUtils';

/* eslint-disable complexity */
function getThresholdConfig(thresholds) {
    if (_.isEmpty(thresholds)) {
        return {
            thresholdErrorKey: 'invalidThresholdConfig',
        };
    }

    return { thresholdConfig: thresholds[0] };
}

function getMetricSource(metricRequests, metrics) {
    if (
        metricRequests.length !== 1 ||
        metricRequests[0].metrics.length < 1 ||
        metricRequests[0].metrics.length > 2
    ) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource], true)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

export default function NumberWithIndicatorWrapper(props) {
    const {
        colorWhenBad,
        colorWhenGood,
        expectValueOverThreshold,
        isTime,
        linkConfig,
        timeFormat,
        suffix = '',
        footnoteLabel,
        textAlign = 'inherit',
    } = props.configuration;

    const { thresholdConfig, thresholdErrorKey } = getThresholdConfig(
        props.thresholds
    );
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props.metricRequests, props.metrics);

    const errorMessageKey = getErrorMessageKey(errorKey, thresholdErrorKey);
    if (errorMessageKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorMessageKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const metric = footnoteLabel?.metric
        ? metricRequest.metrics.filter((x) => x !== footnoteLabel?.metric)[0]
        : metricRequest.metrics[0];
    const formatter = isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const value = metricSource.metricSeries[0]?.[metric];
    const threshold = thresholdConfig.value;

    const labelValue = metricSource.metricSeries[0]?.[footnoteLabel?.metric];
    const formattedLabel =
        !footnoteLabel || (!labelValue && labelValue !== 0)
            ? ''
            : `${footnoteLabel.label} ${labelValue}`;

    const formattedValue = getFormattedGaugeValue(
        value,
        formatter,
        suffix,
        timeFormat
    );

    const formattedThreshold = getFormattedThresholdValue(
        thresholdConfig,
        formatter,
        suffix,
        timeFormat
    );

    const indicatorStatus = getIndicatorStatus(
        value,
        threshold,
        expectValueOverThreshold
    );

    const indicatorColor = getIndicatorColorFromStatus(
        indicatorStatus,
        colorWhenBad,
        colorWhenGood
    );

    return (
        <NumberWithIndicator
            footnoteLabel={formattedLabel}
            textAlign={textAlign}
            indicatorColor={indicatorColor}
            linkConfig={linkConfig}
            metricRequest={metricRequest}
            openLinksInNewTab={props.openLinksInNewTab}
            threshold={formattedThreshold}
            value={formattedValue}
        />
    );
}
/* eslint-enable complexity */

NumberWithIndicatorWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    metricRequests: PropTypes.instanceOf(Array).isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
};
